import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError, apiClient } from '../../core/api';
import type { ApiUser, WalletSnapshot } from '../../core/api';

export function useSettingsAccount() {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const epoch = useRef(0);
  const userId = useRef<string | null>(null);
  const invalidate = useCallback(() => {
    ++epoch.current;
  }, []);

  const reload = useCallback(async () => {
    const version = ++epoch.current;
    // Keep mounted controls stable while focus refresh verifies the session.
    setError(null);
    setBusy(true);
    try {
      const current = await apiClient.currentUser();
      if (version !== epoch.current) return;
      if (current.status === 'disabled') {
        userId.current = null;
        setUser(null);
        setWallet(null);
        return;
      }
      if (userId.current !== current.id) setWallet(null);
      userId.current = current.id;
      setUser(current);
      const balance = await apiClient.wallet();
      if (version === epoch.current) setWallet(balance);
    } catch (requestError) {
      if (version !== epoch.current) return;
      if (requestError instanceof ApiClientError && requestError.status === 401) {
        userId.current = null;
        setUser(null);
        setWallet(null);
      } else {
        setError(requestError instanceof Error ? requestError.message : '账户加载失败');
      }
    } finally {
      if (version === epoch.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    const refresh = () => {
      void reload();
    };
    window.addEventListener('focus', refresh);
    return () => {
      invalidate();
      window.removeEventListener('focus', refresh);
    };
  }, [reload, invalidate]);

  return { user, wallet, busy, error };
}
